// components/PlotTypeSelection.jsx
import React from "react";
import { Card, CardBody, CardHeader } from "reactstrap";
import { AiOutlineInfoCircle } from "react-icons/ai";
import { Tooltip } from "react-tooltip";
import { motion } from "framer-motion";
import PropTypes from "prop-types";

const PlotTypeSelection = ({ selectedType, onTypeChange, className = "" }) => {
  const plotTypes = [
    {
      value: "nuc_comp_single",
      label: "Nucleotide Composition",
      icon: "ri-line-chart-line",
      description: "Single transcript analysis",
      tooltip:
        "Used to explore the nucleotide composition of an individual transcript. Enter a transcript ID and view sliding window analysis of nucleotide compositions, GC content and minimum free energy (MFE).",
    },
    {
      value: "fetch_seq",
      label: "Fetch Sequences",
      icon: "ri-rna-line",
      description: "Extract FASTA sequences",
      tooltip:
        "Enter a comma separated list of transcripts and receive sequences in fasta format. Can be restricted to regions like 5' Leader, CDS, 3' trailer, or limited to specific nucleotide ranges.",
    },
    {
      value: "gene_count",
      label: "Gene Count",
      icon: "ri-bar-chart-2-line",
      description: "Transcriptome statistics",
      tooltip:
        "Displays the number of annotated genes and transcripts in a transcriptome. These are broken down by coding (green) and non-coding (red) genes/transcripts.",
    },
    {
      value: "orfstats",
      label: "ORF Table",
      icon: "ri-table-line",
      description: "ORF analysis table",
      tooltip:
        "Generate a table of uORFs or other ORF types with information such as start/stop positions, length and start codon. Results can be downloaded as CSV.",
    },
    {
      value: "codon_usage",
      label: "Codon Usage",
      icon: "ri-code-s-slash-line",
      description: "Codon frequency analysis",
      tooltip:
        "Displays the codon counts of coding regions of all principal isoforms. A custom list of transcripts can also be entered to get specific codon counts.",
    },
  ];

  // EIRNA brand color
  const EIRNA_COLOR = "#2F9E37";

  const InfoTooltip = ({ id, content, placement = "top" }) => (
    <>
      <AiOutlineInfoCircle
        data-tooltip-id={id}
        title={content}
        style={{
          marginLeft: "4px",
          cursor: "help",
          color: "#6B7280",
          fontSize: "16px",
          verticalAlign: "middle",
          display: "inline-flex",
          alignItems: "center",
        }}
      />
    </>
  );

  const handleTypeSelect = (typeValue) => {
    onTypeChange(typeValue);
  };

  return (
    <Card className={`border-0 shadow-sm ${className}`}>
      <CardHeader className="py-2">
        <div className="d-flex align-items-center">
          <i
            className="ri-apps-2-line me-2 text-muted"
            style={{ fontSize: "1rem" }}
          />
          <h5 className="mb-0 fw-semibold" style={{ fontSize: "1rem" }}>
            Choose Plot Type
          </h5>
        </div>
      </CardHeader>

      <CardBody className="py-2">
        {/* Plot Type Pills */}
        <div className="d-flex flex-wrap gap-2">
          {plotTypes.map((plotType, index) => (
            <motion.button
              key={plotType.value}
              type="button"
              className="btn position-relative"
              onClick={() => handleTypeSelect(plotType.value)}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{
                delay: index * 0.03,
                type: "spring",
                stiffness: 200,
                damping: 20,
              }}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              style={{
                backgroundColor:
                  plotType.value === selectedType ? EIRNA_COLOR : "transparent",
                borderColor: EIRNA_COLOR,
                color: plotType.value === selectedType ? "white" : EIRNA_COLOR,
                fontWeight: plotType.value === selectedType ? "600" : "500",
                padding: "8px 14px",
                borderRadius: "20px",
                fontSize: "12px",
                border: `2px solid ${EIRNA_COLOR}`,
                cursor: "pointer",
                transition: "all 0.2s ease",
                whiteSpace: "nowrap",
                outline: "none",
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                minHeight: "36px",
                boxShadow:
                  plotType.value === selectedType
                    ? `0 4px 12px ${EIRNA_COLOR}30`
                    : "0 1px 3px rgba(0,0,0,0.05)",
              }}
              onMouseEnter={(e) => {
                if (plotType.value !== selectedType) {
                  e.target.style.backgroundColor = `${EIRNA_COLOR}08`;
                  e.target.style.transform = "translateY(-1px)";
                  e.target.style.boxShadow = `0 4px 12px ${EIRNA_COLOR}20`;
                }
              }}
              onMouseLeave={(e) => {
                if (plotType.value !== selectedType) {
                  e.target.style.backgroundColor = "transparent";
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow = "0 1px 3px rgba(0,0,0,0.05)";
                }
              }}
              aria-pressed={plotType.value === selectedType}
              aria-label={`Select ${plotType.label} plot type`}
            >
              {/* Icon */}
              <i
                className={plotType.icon}
                style={{
                  fontSize: "14px",
                  display: "flex",
                  alignItems: "center",
                }}
              />

              {/* Label Container */}
              <span
                style={{
                  fontSize: "12px",
                  display: "flex",
                  alignItems: "center",
                  gap: "2px",
                }}
              >
                {plotType.label}
                <InfoTooltip
                  id={`tt-${plotType.value}`}
                  content={plotType.tooltip}
                  placement="top"
                />
              </span>

              {/* Selection Indicator */}
              {plotType.value === selectedType && (
                <motion.span
                  style={{
                    position: "absolute",
                    top: "-6px",
                    right: "-6px",
                    width: "18px",
                    height: "18px",
                    backgroundColor: "rgba(255,255,255,0.95)",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "10px",
                    color: EIRNA_COLOR,
                    fontWeight: "bold",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                    border: `1px solid ${EIRNA_COLOR}20`,
                  }}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{
                    delay: 0.1,
                    type: "spring",
                    stiffness: 400,
                    damping: 25,
                  }}
                >
                  <i className="ri-check-line" style={{ fontSize: "10px" }} />
                </motion.span>
              )}
            </motion.button>
          ))}
        </div>
      </CardBody>
    </Card>
  );
};

PlotTypeSelection.propTypes = {
  selectedType: PropTypes.string,
  onTypeChange: PropTypes.func.isRequired,
  className: PropTypes.string,
};

export default PlotTypeSelection;
